/**
 * A casca: trilho de navegação, barra superior e área de conteúdo.
 *
 * A arquitetura de informação é a do protótipo — as mesmas nove seções, na
 * mesma ordem, com o mesmo subtítulo. Ela não foi reinventada: já estava
 * validada, e a ordem reflete dependência real entre os módulos.
 */
import { useEffect, useState, type ReactNode } from "react";
import type { Ator } from "./api.js";
import { aplicarTema, lerTema, TEMAS, type IdTema } from "./tema.js";

export interface Secao {
  id: string;
  nome: string;
  sub: string;
  /** Perfis que enxergam a seção. Vazio = todos. */
  perfis?: Array<Ator["perfil"]>;
  /**
   * Seções filhas, exibidas indentadas sob a mãe e sempre visíveis.
   *
   * Ficam sempre à vista, não escondidas atrás de um clique na mãe: o
   * cadastro de produto existia na API desde o começo e ninguém achou,
   * porque não havia nada no trilho apontando para ele. Submenu que só
   * aparece depois de descobrir a seção mãe repete o mesmo problema.
   */
  filhos?: Secao[];
}

export const SECOES: Secao[] = [
  { id: "painel", nome: "Painel", sub: "Como estão seus números e o que falta para cada um" },
  { id: "numeros", nome: "Números", sub: "Cadastro, ciclo de vida e ficha de cada número" },
  {
    id: "planos",
    nome: "Planos de aquecimento",
    sub: "Etapas, limites e critérios — configuráveis sem alterar código",
    filhos: [
      {
        id: "planos/mensagens",
        nome: "Mensagens",
        sub: "O que os números dizem um ao outro enquanto aquecem",
        perfis: ["administrador", "gerente"],
      },
    ],
  },
  { id: "fila", nome: "Fila de execução", sub: "Toda atividade programada, executada ou bloqueada por regra" },
  {
    id: "campanhas",
    nome: "Campanhas",
    sub: "Disparos com os números que a Incubadora liberou",
    filhos: [
      {
        id: "campanhas/produtos",
        nome: "Produtos",
        sub: "O que você vende — e a chave do descanso “mesmo produto, mesmo contato”",
        perfis: ["administrador", "gerente"],
      },
      {
        id: "campanhas/kits",
        nome: "Kits",
        sub: "As variações da mensagem — sem cinco delas, nenhuma campanha é aprovada",
        perfis: ["administrador", "gerente"],
      },
    ],
  },
  {
    id: "leads",
    nome: "Leads",
    sub: "Quem respondeu, de qual campanha veio e quem atende",
    filhos: [
      {
        id: "leads/conversas",
        nome: "Conversas",
        sub: "O que está sendo dito — por contato, somando todos os números",
      },
    ],
  },
  {
    id: "crm",
    nome: "CRM",
    sub: "Seus clientes no funil — do primeiro contato ao fechamento",
  },
  { id: "historico", nome: "Histórico", sub: "Registro de decisões automáticas e manuais" },
  { id: "usuarios", nome: "Usuários", sub: "Hierarquia, funções e o escopo de cada um", perfis: ["administrador", "gerente"] },
  { id: "config", nome: "Configurações", sub: "Tema, integração, empresa e política de descanso" },
];

/** Mãe e filhas numa lista só — para casar rota e achar o título do cabeçalho. */
export function todasAsSecoes(): Secao[] {
  return SECOES.flatMap((s) => [s, ...(s.filhos ?? [])]);
}

export function rotaAtual(): string {
  const p = window.location.pathname.replace(/^\/+|\/+$/g, "");
  return todasAsSecoes().some((s) => s.id === p) ? p : "painel";
}

export function irPara(id: string): void {
  window.history.pushState({}, "", `/${id}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useRota(): string {
  const [rota, setRota] = useState(rotaAtual);
  useEffect(() => {
    const ouvir = (): void => setRota(rotaAtual());
    window.addEventListener("popstate", ouvir);
    return () => window.removeEventListener("popstate", ouvir);
  }, []);
  return rota;
}

function SeletorDeTema(): JSX.Element {
  const [tema, setTema] = useState<IdTema>(lerTema);
  return (
    <select
      aria-label="Tema"
      value={tema}
      onChange={(e) => {
        const v = e.target.value as IdTema;
        setTema(v);
        aplicarTema(v);
      }}
      className="bg-superficie-2 border border-linha rounded-lg text-[11px] text-tinta-2 px-2 py-1.5 outline-none focus:border-marca"
    >
      {TEMAS.map((t) => (
        <option key={t.id} value={t.id}>
          {t.nome}
        </option>
      ))}
    </select>
  );
}

export function Casca({
  ator,
  aoSair,
  children,
}: {
  ator: Ator;
  aoSair: () => void;
  children: ReactNode;
}): JSX.Element {
  const rota = useRota();
  const secao = todasAsSecoes().find((s) => s.id === rota) ?? SECOES[0]!;
  const pode = (s: Secao): boolean => !s.perfis || s.perfis.includes(ator.perfil);
  const visiveis = SECOES.filter(pode).map((s) => ({
    ...s,
    filhos: (s.filhos ?? []).filter(pode),
  }));

  return (
    <div className="min-h-full grid grid-cols-1 lg:grid-cols-[236px_1fr]">
      {/* ─── Trilho ─────────────────────────────────────────────── */}
      <aside className="hidden lg:flex flex-col border-r border-linha bg-superficie">
        <div className="px-5 h-14 flex items-center gap-2.5 border-b border-linha">
          <div className="grid place-items-center size-7 rounded-lg bg-marca text-superficie text-[11px] font-bold">
            AD
          </div>
          <div className="leading-tight">
            <strong className="block text-[13px] tracking-tight">AutoDis</strong>
            <span className="block text-[10.5px] text-tinta-3">
              Incubadora e Disparo
            </span>
          </div>
        </div>

        <nav aria-label="Seções" className="p-2.5 flex-1 overflow-y-auto">
          {visiveis.map((s) => {
            const ativa = s.id === rota;
            return (
              <div key={s.id} className="mb-0.5">
                <button
                  onClick={() => irPara(s.id)}
                  aria-current={ativa ? "page" : undefined}
                  className={`w-full text-left rounded-lg px-3 py-2 text-[13px] transition-colors ${
                    ativa
                      ? "bg-marca-suave text-marca-tinta font-medium"
                      : "text-tinta-2 hover:bg-superficie-3 hover:text-tinta"
                  }`}
                >
                  {s.nome}
                </button>
                {s.filhos.length > 0 && (
                  <div className="ml-3 pl-2 border-l border-linha mt-0.5">
                    {s.filhos.map((f) => {
                      const fa = f.id === rota;
                      return (
                        <button
                          key={f.id}
                          onClick={() => irPara(f.id)}
                          aria-current={fa ? "page" : undefined}
                          className={`w-full text-left rounded-lg px-3 py-1.5 text-[12.5px] transition-colors ${
                            fa
                              ? "bg-marca-suave text-marca-tinta font-medium"
                              : "text-tinta-3 hover:bg-superficie-3 hover:text-tinta-2"
                          }`}
                        >
                          {f.nome}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-linha">
          <SeletorDeTema />
        </div>
      </aside>

      {/* ─── Conteúdo ───────────────────────────────────────────── */}
      <main className="min-w-0 flex flex-col">
        <header className="border-b border-linha bg-superficie px-5 lg:px-7 h-14 flex items-center gap-4">
          <div className="min-w-0">
            <h1 className="text-[15px] font-semibold tracking-tight leading-tight truncate">
              {secao.nome}
            </h1>
            <p className="text-[11.5px] text-tinta-3 truncate hidden sm:block">
              {secao.sub}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3 shrink-0">
            <div className="text-right hidden sm:block leading-tight">
              <div className="text-[12.5px] text-tinta-2">{ator.nome}</div>
              <div className="text-[10px] uppercase tracking-[0.08em] text-tinta-3">
                {ator.perfil}
              </div>
            </div>
            <button
              onClick={aoSair}
              className="text-[12.5px] text-tinta-2 hover:text-tinta rounded-md px-2 py-1"
            >
              Sair
            </button>
          </div>
        </header>

        {/* Navegação em telas estreitas */}
        <div className="lg:hidden border-b border-linha bg-superficie overflow-x-auto">
          <div className="flex gap-1 px-3 py-2">
            {visiveis.flatMap((s) => [s, ...s.filhos]).map((s) => (
              <button
                key={s.id}
                onClick={() => irPara(s.id)}
                className={`shrink-0 rounded-lg px-3 py-1.5 text-xs ${
                  s.id === rota
                    ? "bg-marca-suave text-marca-tinta font-medium"
                    : "text-tinta-2"
                }`}
              >
                {s.nome}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 p-5 lg:p-7 max-w-[1400px] w-full">{children}</div>
      </main>
    </div>
  );
}
